export const API_BASE_URL = '/api'

/**
 * Safely extract a human-readable error message from any thrown value.
 *
 * Handles Axios-style errors (err.response.data.message / .error),
 * standard Error objects, plain strings, and unknown objects that would
 * otherwise render as "[object Object]" in the UI.
 */
export function getErrorMessage(err: unknown, fallback = 'An unexpected error occurred'): string {
  if (err == null) return fallback

  // Already a string — use directly
  if (typeof err === 'string') return err

  // Object-shaped error (Error, AxiosError, or plain object)
  if (typeof err === 'object') {
    const e = err as Record<string, any>

    // Axios-style: err.response.data.message / err.response.data.error
    const resData = e.response?.data
    if (resData) {
      if (typeof resData.message === 'string' && resData.message) return resData.message
      if (typeof resData.error === 'string' && resData.error) return resData.error
      if (typeof resData.error === 'object' && resData.error !== null) {
        if (typeof resData.error.message === 'string' && resData.error.message) return resData.error.message
        return JSON.stringify(resData.error)
      }
      if (typeof resData === 'string') return resData
    }

    // Standard Error.message
    if (typeof e.message === 'string' && e.message) return e.message

    // Last resort: stringify, but guard against circular refs
    try {
      const json = JSON.stringify(err)
      if (json && json !== '{}') return json
    } catch {
      // circular reference — fall through
    }
  }

  return fallback
}

interface FetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean>
}

export const apiFetch = async (
  endpoint: string,
  options: FetchOptions = {}
): Promise<Response> => {
  const { params, ...fetchOptions } = options

  let url = `${API_BASE_URL}${endpoint}`

  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value))
    })
    url += `?${searchParams.toString()}`
  }

  const headers = new Headers(fetchOptions.headers || {})

  // Inject auth token if available from AuthContext
  const token = localStorage.getItem('auth_token')
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  headers.set('Content-Type', 'application/json')

  const response = await fetch(url, {
    ...fetchOptions,
    headers
  })

  if (!response.ok) {
    let message = `API Error: ${response.status}`
    try {
      const errorBody = await response.json()
      if (typeof errorBody.message === 'string' && errorBody.message) {
        message = errorBody.message
      } else if (errorBody.error) {
        if (typeof errorBody.error === 'string') {
          message = errorBody.error
        } else if (typeof errorBody.error === 'object' && errorBody.error !== null) {
          message = (typeof errorBody.error.message === 'string' && errorBody.error.message)
            ? errorBody.error.message
            : JSON.stringify(errorBody.error)
        }
      }
    } catch {
      // Response body not JSON, use default message
    }
    // Attach the HTTP status so callers can branch on it (e.g. silence
    // background polling on 401). Existing callers that just read .message
    // are unaffected.
    const err: Error & { status?: number } = new Error(message)
    err.status = response.status
    throw err
  }

  return response
}

export const apiGet = async <T = any>(
  endpoint: string,
  params?: Record<string, string | number | boolean>
): Promise<T> => {
  const response = await apiFetch(endpoint, { method: 'GET', params })
  return response.json()
}

export const apiPost = async <T = any>(
  endpoint: string,
  body?: any,
  params?: Record<string, string | number | boolean>
): Promise<T> => {
  const response = await apiFetch(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
    params
  })
  return response.json()
}

export const apiPut = async <T = any>(
  endpoint: string,
  body?: any,
  params?: Record<string, string | number | boolean>
): Promise<T> => {
  const response = await apiFetch(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
    params
  })
  return response.json()
}

export const apiDelete = async <T = any>(
  endpoint: string,
  params?: Record<string, string | number | boolean>
): Promise<T> => {
  const response = await apiFetch(endpoint, {
    method: 'DELETE',
    params
  })
  return response.json()
}

/* ─── Tickets (email-first / Zoho-Desk model) ──────────────────────────────── */

export type TicketStatus = 'open' | 'answered' | 'closed'
export type TicketChannel = 'chat' | 'email'
export type TicketDirection = 'in' | 'out'

export interface Ticket {
  id: number | string
  ticket_number: string
  channel: TicketChannel
  status: TicketStatus
  assigned_agent_id: number | string | null
  assigned_agent_name: string | null
  visitor_name: string | null
  visitor_email: string | null
  subject: string | null
  last_message_at: string | null
  last_direction: TicketDirection | null
  unread: boolean
  gmail_thread_id: string | null
  created_at: string
}

export interface TicketMessageAttachment {
  url: string
  name: string
  type: string
  size: number
}

export interface TicketMessage {
  id: number | string
  sender_type: 'customer' | 'agent' | 'system'
  sender_id: number | string | null
  body: string
  message_type?: string
  created_at: string
  attachments?: TicketMessageAttachment[]
}

export interface TicketTranscriptEntry {
  sender_type: string
  sender_id: string | number | null
  body: string
  created_at: string
}

export interface TicketEvent {
  id: number | string
  type: string
  body: string | null
  meta: any
  actor: string | null
  created_at: string
}

export interface TicketDetail extends Ticket {
  chat_id?: number | string | null
  messages: TicketMessage[]
  events?: TicketEvent[]
  transcript?: TicketTranscriptEntry[]
}

export interface TicketListResponse {
  tickets: Ticket[]
  total: number
  page: number
  pageSize: number
}

/** Assignment filter: 'me' | 'unassigned' | a specific agent id. */
export type TicketAssignedFilter = 'me' | 'unassigned' | string

export interface TicketListParams {
  status?: TicketStatus | ''
  channel?: TicketChannel | ''
  assigned?: TicketAssignedFilter | ''
  page?: number
  pageSize?: number
}

export const getTickets = async (params: TicketListParams = {}): Promise<TicketListResponse> => {
  const clean: Record<string, string | number | boolean> = {}
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') clean[k] = v as string | number | boolean
  })
  return apiGet<TicketListResponse>('/tickets', clean)
}

export const getTicket = async (id: string | number): Promise<TicketDetail> =>
  apiGet<TicketDetail>(`/tickets/${id}`)

/**
 * Look up the ticket associated with a chat, if any.
 *
 * The backend returns 404 when the chat has no ticket (a normal live chat),
 * so we treat that as "no ticket" and resolve to null instead of throwing.
 * Any other error still propagates.
 */
export const getTicketByChat = async (
  chatId: string | number
): Promise<Ticket | null> => {
  try {
    return await apiGet<Ticket>(`/tickets/by-chat/${chatId}`)
  } catch (err) {
    if ((err as { status?: number })?.status === 404) return null
    throw err
  }
}

export interface TicketReplyBody {
  text: string
  html?: string
  attachments?: TicketMessageAttachment[]
}

/** Send an in-thread email reply to the customer + store an agent message. */
export const replyTicket = async (
  id: string | number,
  body: TicketReplyBody
): Promise<{ ticket: Ticket; message: TicketMessage }> =>
  apiPost<{ ticket: Ticket; message: TicketMessage }>(`/tickets/${id}/reply`, body)

/** Claim a ticket for the current agent. Throws (409) if already claimed. */
export const claimTicket = async (id: string | number): Promise<Ticket> =>
  apiPost<Ticket>(`/tickets/${id}/claim`)

/** Assign a ticket to an agent. Empty string unassigns. */
export const assignTicket = async (
  id: string | number,
  agentId: string | number | ''
): Promise<Ticket> =>
  apiPost<Ticket>(`/tickets/${id}/assign`, { agent_id: agentId })

/** Set ticket status (open / answered / closed). */
export const setTicketStatus = async (
  id: string | number,
  status: TicketStatus
): Promise<Ticket> =>
  apiPost<Ticket>(`/tickets/${id}/status`, { status })

// File upload via multipart form data (no JSON Content-Type header)
export const apiUploadFile = async <T = any>(
  endpoint: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<T> => {
  const token = localStorage.getItem('auth_token')
  const url = `${API_BASE_URL}${endpoint}`

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const formData = new FormData()
    formData.append('file', file)

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100))
        }
      })
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch {
          reject(new Error('Invalid response from server'))
        }
      } else {
        let errMsg = `Upload failed: ${xhr.status}`
        try {
          const body = JSON.parse(xhr.responseText)
          if (typeof body.message === 'string' && body.message) errMsg = body.message
          else if (typeof body.error === 'string' && body.error) errMsg = body.error
        } catch { /* not JSON */ }
        reject(new Error(errMsg))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Upload failed')))
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))

    xhr.open('POST', url)
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.send(formData)
  })
}

/* ─── Agent file upload + signature ────────────────────────────────────────── */

/** Server cap for agent uploads (POST /agent/upload) — keep in sync with the API. */
export const AGENT_UPLOAD_MAX_BYTES = 25 * 1024 * 1024 // 25 MB

export interface UploadedFile {
  id: number | string
  url: string
  type: string
  name: string
  size: number
}

/**
 * Upload a file as the authenticated agent.
 * Returns { id, url, type, name, size } — usable directly as a reply attachment.
 */
export const agentUploadFile = async (
  file: File,
  onProgress?: (percent: number) => void
): Promise<UploadedFile> => apiUploadFile<UploadedFile>('/agent/upload', file, onProgress)

/** Get the caller's own ticket-reply signature (HTML, or null). */
export const getMySignature = async (): Promise<{ signature: string | null }> =>
  apiGet<{ signature: string | null }>('/agents/me/signature')

/** Save (or clear, with empty string) the caller's ticket-reply signature. */
export const setMySignature = async (signature: string): Promise<{ ok: boolean }> =>
  apiPut<{ ok: boolean }>('/agents/me/signature', { signature })

/* ─── AI Copilot ────────────────────────────────────────────────────────────
 *
 * A grounded Q&A assistant. Conversations are persisted server-side as
 * "sessions"; answers stream back as Server-Sent Events from the /ask
 * endpoint (consumed via fetch + a ReadableStream reader, see CopilotChat).
 */

export interface CopilotSession {
  id: number | string
  title: string
  last_message_at: string | null
  created_at: string
}

export interface CopilotCitation {
  page_title: string
  anchor: string
}

export interface CopilotAttachment {
  url: string
  type?: string
  name?: string
}

export interface CopilotMessage {
  id: number | string
  role: 'user' | 'assistant'
  content: string
  citations: CopilotCitation[] | null
  attachments?: CopilotAttachment[]
  created_at: string
}

export interface CopilotSessionDetail {
  id: number | string
  title: string
  messages: CopilotMessage[]
}

export interface CopilotSkillInfo {
  version: number | string
  article_count: number
  filename: string
  created_at: string
}

export interface CopilotSkillUploadResult {
  version: number | string
  added: number
  changed: number
  carried_over: number
  article_count: number
}

export const getCopilotSessions = async (): Promise<CopilotSession[]> =>
  apiGet<CopilotSession[]>('/copilot/sessions')

export const createCopilotSession = async (): Promise<{ id: number | string }> =>
  apiPost<{ id: number | string }>('/copilot/sessions')

export const getCopilotSession = async (
  id: number | string
): Promise<CopilotSessionDetail> => apiGet<CopilotSessionDetail>(`/copilot/sessions/${id}`)

/** PATCH a session title. apiFetch handles auth + JSON; PATCH has no helper. */
export const patchCopilotSession = async (
  id: number | string,
  title: string
): Promise<{ ok: boolean }> => {
  const response = await apiFetch(`/copilot/sessions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  })
  return response.json()
}

export const deleteCopilotSession = async (
  id: number | string
): Promise<{ ok: boolean }> => apiDelete<{ ok: boolean }>(`/copilot/sessions/${id}`)

export const getCopilotSkill = async (): Promise<CopilotSkillInfo> =>
  apiGet<CopilotSkillInfo>('/copilot/skill')

export const uploadCopilotSkill = async (
  file: File,
  onProgress?: (percent: number) => void
): Promise<CopilotSkillUploadResult> =>
  apiUploadFile<CopilotSkillUploadResult>('/copilot/skill', file, onProgress)

/** Discriminated SSE event shapes emitted by POST /copilot/sessions/:id/ask. */
export type CopilotStreamEvent =
  | { type: 'delta'; text: string }
  | { type: 'done'; messageId: number; citations: CopilotCitation[]; fallback: boolean }
  | { type: 'error'; code: string; message: string }

export interface AskCopilotHandlers {
  onDelta: (text: string) => void
  onDone: (
    messageId: number,
    citations: CopilotCitation[],
    fallback: boolean
  ) => void
  onError: (code: string, message: string) => void
  signal?: AbortSignal
}

/**
 * Ask a question in a session and consume the SSE response.
 *
 * We can't use EventSource here: it only does GET and can't set the
 * Authorization header. Instead we POST with fetch, then read the body as a
 * stream — decoding bytes, buffering until we have complete `\n\n`-delimited
 * SSE records, and parsing each `data:` line as JSON.
 *
 * The "AI not configured" case (HTTP 503) arrives *before* the stream opens
 * as a normal JSON response, so we branch on response.ok first.
 */
export const askCopilot = async (
  sessionId: number | string,
  question: string,
  handlers: AskCopilotHandlers,
  attachments?: CopilotAttachment[]
): Promise<void> => {
  const token = localStorage.getItem('auth_token')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  }
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(`${API_BASE_URL}/copilot/sessions/${sessionId}/ask`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ question, attachments: attachments && attachments.length ? attachments : undefined }),
    signal: handlers.signal,
  })

  // 503 / non-stream error responses arrive as JSON before the stream opens.
  if (!response.ok || !response.body) {
    let code = 'STREAM_ERROR'
    let message = `Request failed (${response.status})`
    try {
      const body = await response.json()
      if (typeof body.code === 'string') code = body.code
      if (typeof body.message === 'string' && body.message) message = body.message
      else if (typeof body.error === 'string' && body.error) message = body.error
    } catch {
      /* not JSON */
    }
    handlers.onError(code, message)
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const dispatch = (raw: string) => {
    // An SSE record may have multiple lines; collect the data: payloads.
    const dataLines = raw
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trim())
    if (dataLines.length === 0) return
    const payload = dataLines.join('\n')
    if (!payload || payload === '[DONE]') return
    let evt: CopilotStreamEvent
    try {
      evt = JSON.parse(payload)
    } catch {
      return
    }
    if (evt.type === 'delta') handlers.onDelta(evt.text)
    else if (evt.type === 'done') handlers.onDone(evt.messageId, evt.citations || [], !!evt.fallback)
    else if (evt.type === 'error') handlers.onError(evt.code, evt.message)
  }

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep: number
    // Records are separated by a blank line (\n\n).
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const record = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      if (record.trim()) dispatch(record)
    }
  }
  // Flush any trailing record without a final blank line.
  if (buffer.trim()) dispatch(buffer)
}

/* ─── Visitor profile (live map / list "View details" drawer) ───────────────
 *
 * Aggregated profile for a single visitor: total time with us, every chat
 * (linked by email — anonymous visitors have none), and every company the
 * visitor has appeared under. Backed by GET /visitors/:id/profile.
 */

export interface VisitorProfileCompany {
  alias: string
  company_name: string | null
  company_logo_url: string | null
  country: string | null
}

export interface VisitorProfileChat {
  id: string | number
  channel: string
  status: string | null
  account_alias: string | null
  company_name: string | null
  created_at: string | null
  last_message_at: string | null
  message_count: number
  preview: string
}

export interface VisitorProfile {
  id: string
  name: string | null
  email: string | null
  account_alias: string | null
  total_time_seconds: number
  visit_count: number
  first_seen: string | null
  last_seen: string | null
  current_page_url: string | null
  current_page_title: string | null
  status: 'online' | 'offline'
  is_online: boolean
  conversation_count?: number
  companies: VisitorProfileCompany[]
  chats: VisitorProfileChat[]
}

export const getVisitorProfile = async (id: string): Promise<VisitorProfile> =>
  apiGet<VisitorProfile>(`/visitors/${id}/profile`)

/* ─── Chat transcript ───────────────────────────────────────────────────────
 *
 * A single chat's messages, used to render a transcript. Mirrors the shape the
 * dashboard's chat thread already consumes (GET /chats/:id/messages).
 */

export interface ChatTranscriptMessage {
  id: string | number
  chat_id?: string | number
  sender_type?: string
  sender_name?: string
  body?: string
  message_type?: string
  created_at: string
  attachments?: any[]
}

export interface ChatMessagesResponse {
  messages: ChatTranscriptMessage[]
  cursor?: string | null
  status?: string
  assigned_agent?: { agent_id: string; agent_name: string; agent_avatar?: string } | null
}

export const getChatMessages = async (
  chatId: string | number
): Promise<ChatMessagesResponse> => apiGet<ChatMessagesResponse>(`/chats/${chatId}/messages`)

/* ─── Bulk actions (multi-select on chat + ticket lists) ─────────────────────
 *
 * Each endpoint takes an array of ids and returns a summary. Delete is admin/
 * supervisor-gated for chats (the API enforces it; a 403 surfaces as an error).
 */

export interface BulkResult {
  requested: number
  deleted?: number
  assigned?: number
  deleted_tickets?: number
  results?: Array<{ id: string; ok: boolean; error?: string }>
}

/** Permanently delete the given chats (+ their messages/tickets). */
export const bulkDeleteChats = async (ids: Array<string | number>): Promise<BulkResult> =>
  apiPost<BulkResult>('/chats/bulk-delete', { ids })

/** Assign the given chats to one agent. */
export const bulkAssignChats = async (
  ids: Array<string | number>,
  agentId: string | number
): Promise<BulkResult> => apiPost<BulkResult>('/chats/bulk-assign', { ids, agent_id: agentId })

/** Permanently delete the given tickets (+ their events). */
export const bulkDeleteTickets = async (ids: Array<string | number>): Promise<BulkResult> =>
  apiPost<BulkResult>('/tickets/bulk-delete', { ids })

/** Assign the given tickets to one agent (null/empty = unassign). */
export const bulkAssignTickets = async (
  ids: Array<string | number>,
  agentId: string | number | null
): Promise<BulkResult> =>
  apiPost<BulkResult>('/tickets/bulk-assign', { ids, agent_id: agentId ?? '' })

/* ─── Resolution notes / escalations ───────────────────────────────────────── */

export interface ResolutionNote {
  chat_id: string | number
  status: string | null
  resolution_summary: string
  resolution_tag: string | null
  closed_at: string | null
  created_at: string | null
  visitor_name: string | null
  visitor_email: string | null
  account_alias: string | null
  company_name: string | null
  company_logo_url: string | null
  agent_id: string | number | null
  escalated: boolean
  escalated_at: string | null
}

export const getResolutionNotes = async (): Promise<{ notes: ResolutionNote[] }> =>
  apiGet<{ notes: ResolutionNote[] }>('/resolution-notes')

export const escalateChat = async (
  chatId: string | number,
  escalated: boolean
): Promise<{ chat_id: string | number; escalated: boolean }> =>
  apiPost(`/chats/${chatId}/escalate`, { escalated })
